import { NotAuthenticated } from '@feathersjs/errors';

import { globalCache } from '@sbrc/utils';

import { getApp } from '../../utils';

import { captureMustChangePin } from './mustChangePin';
import { userIDStore, type UserIDStore } from './userIDStore';

/**
 * One-time wiring of the `userIDStore` to the feathers-client auth
 * lifecycle:
 *
 * 1. `reAuthenticate()` on first access (silent restore from the JWT
 *    in localStorage, if any).
 * 2. An error hook that clears `userIDStore` on any service-level
 *    `NotAuthenticated` — `PrivatePage` observes this and navigates to
 *    `/login`.
 *
 * `globalCache` makes this idempotent: the side effects fire exactly
 * once per page, on the first call to `currentUserID()`. Subsequent
 * calls just return the store.
 *
 * Note: the wiring intentionally lives behind a *separate* cache key
 * from the store itself (which lives in `userIDStore.ts`) so that
 * callers like `invalidateSession` — which only need to write to the
 * store — can import `userIDStore` without dragging in `getApp` and
 * the import cycle that follows.
 */
function wireOnce(): void {
  globalCache('currentUserID-wired', () => {
    const store = userIDStore();

    const reauth = async () => {
      try {
        const result = await getApp().reAuthenticate();
        captureMustChangePin(result.mustChangePin);
        store.set(result.user.id);
      } catch {
        store.set(null);
      }
    };

    reauth();

    getApp().hooks({
      error(context) {
        // listen for any NotAuthenticated errors which happen
        // when calling any service (except authentication itself)
        if (
          context.error instanceof NotAuthenticated &&
          context.path !== 'authentication' &&
          context.path !== 'controlPanelToken'
        ) {
          // Clear current user
          // Inside a <PrivatePage> this will navigate to the login page
          store.set(null);
        }

        return context;
      },
    });

    return true;
  });
}

export function currentUserID(): UserIDStore {
  wireOnce();

  return userIDStore();
}
